'use strict';

const { addClass, queryAll, removeClass, toggleClass } = require('../domUtil.js');
const { CHANGED_CLASS, FILLED_CLASS, OPEN_CLASS, EVENT_KEY_ENTER, EVENT_KEY_SPACE } = require('../constants.js');

const CUSTOM_SELECT_SELECTOR = '.custom-select';

/**
 * Applies `m-changed` / clears `m-changed` + `m-filled` when empty so the floating label matches `.value`.
 * Used from change (capture), focusin, and refresh paths—not only programmatic writes.
 * @param {HTMLSelectElement} select - element matching `.custom-select`
 */
function syncFloatLabel(select) {
    if (!select || !select.matches(CUSTOM_SELECT_SELECTOR)) return;

    if (select.value === '') {
        removeClass(select, CHANGED_CLASS, FILLED_CLASS);
    } else {
        addClass(select, CHANGED_CLASS);
    }
}

/**
 * Custom select – toggle open state on click
 * @param {Object} event Click event
 */
function csClick(event) {
    if (!event.target.matches(CUSTOM_SELECT_SELECTOR)) return;

    toggleClass(event.target, OPEN_CLASS);
}

/**
 * Custom select – remove open state when dropdown loses focus (focusout bubbles, blur does not)
 * @param {Object} event FocusOut event
 */
function csFocusOut(event) {
    if (!event.target.matches(CUSTOM_SELECT_SELECTOR)) return;

    removeClass(event.target, OPEN_CLASS);
}

/**
 * Custom select – focus moves to control (e.g. Tab). Does not fire `change` until value changes;
 * sync modifiers so label animation matches design (`:focus-within`).
 * @param {Object} event FocusEvent (focusin)
 */
function csFocusIn(event) {
    if (!event.target.matches(CUSTOM_SELECT_SELECTOR)) return;

    syncFloatLabel(event.target);
}

/**
 * Custom select – add open state when dropdown is opened via keyboard (Enter or Space)
 * @param {Object} event Keydown event
 */
function csKeydown(event) {
    if (!event.target.matches(CUSTOM_SELECT_SELECTOR)) return;

    if (event.key === EVENT_KEY_ENTER || event.key === EVENT_KEY_SPACE) {
        addClass(event.target, OPEN_CLASS);
    }
}

/**
 * Custom select – change (user selection or script that dispatches a bubbling `change`).
 * Registered in capture phase so delegated listener runs reliably.
 * @param {Object} event Change event
 */
function csCaptureChange(event) {
    const el = event.target;

    if (!el.matches(CUSTOM_SELECT_SELECTOR)) return;

    removeClass(el, OPEN_CLASS);
    syncFloatLabel(el);
}

/**
 * Applies floating-label classes to all .custom-select in the DOM.
 * Safe to call after partial page updates (e.g. cart quantity change).
 */
function enableFloatSelect() {
    queryAll(CUSTOM_SELECT_SELECTOR).forEach(select => {
        addClass(select, 'm-floating-label');

        Array.from(select.options).forEach(option => {
            if (option.value?.length && option.selected) {
                addClass(select, FILLED_CLASS);
            }
        });
    });
}

/**
 * Re-sync m-filled / m-changed for `.custom-select.m-floating-label` under `container`.
 * Base class `m-floating-label` comes from `enableFloatSelect()` — only modifiers are reconciled here.
 * @param {ParentNode} [container] - root to search; default `document`
 */
function refreshFloatLabels(container) {
    const root = container || document;

    queryAll(`${CUSTOM_SELECT_SELECTOR}.m-floating-label`, root).forEach(select => {
        Array.from(select.options).forEach(option => {
            if (option.value?.length && option.selected) {
                addClass(select, FILLED_CLASS);
            }
        });

        syncFloatLabel(select);
    });
}

let domMutationRaf = null;

/** Avoid attaching multiple MutationObservers to the same <form>. */
const floatingLabelObservedForms = new WeakSet();

/**
 * Debounced refresh after form subtree mutations (childList / option selected attribute).
 */
function queueFloatRefresh() {
    if (domMutationRaf !== null) cancelAnimationFrame(domMutationRaf);

    domMutationRaf = requestAnimationFrame(() => {
        domMutationRaf = null;
        enableFloatSelect();
        refreshFloatLabels(document);
    });
}

/**
 * Observes each <form> subtree so carousel/timer noise outside forms does not trigger refreshes.
 * Skips forms already observed (WeakSet).
 */
function attachFormObservers() {
    if (typeof document === 'undefined') return;

    const observer = new MutationObserver(queueFloatRefresh);

    queryAll('form').forEach(form => {
        if (floatingLabelObservedForms.has(form)) return;

        floatingLabelObservedForms.add(form);

        observer.observe(form, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['selected']
        });
    });
}

/**
 * Attaches per-form observers when `document.body` exists; rescans on `load` for late-injected forms.
 */
function initFormWatch() {
    if (!document.body) {
        document.addEventListener('DOMContentLoaded', initFormWatch, { once: true });
        return;
    }

    attachFormObservers();
    queueFloatRefresh();

    window.addEventListener('load', () => {
        attachFormObservers();
        queueFloatRefresh();
    });
}

/**
 * Binds delegated listeners on `document` for current and future `.custom-select` elements.
 */
function bindDocListeners() {
    document.addEventListener('click', csClick);
    document.addEventListener('keydown', csKeydown);
    document.addEventListener('focusin', csFocusIn);
    document.addEventListener('focusout', csFocusOut);
    document.addEventListener('change', csCaptureChange, true);
}

// Called by processInclude on DOMContentLoaded (e.g. main.js, cart page)
function init() {
    bindDocListeners();
    enableFloatSelect();
    initFormWatch();
}

// Re-run after cart/partial DOM updates so new selects get m-floating-label and new forms get observers
document.addEventListener('cart:update', () => {
    enableFloatSelect();
    attachFormObservers();
});

init.enableSelectFloating = enableFloatSelect;
init.syncCustomSelectFloatingLabel = syncFloatLabel;
init.refreshFloatingLabelsIn = refreshFloatLabels;

module.exports = init;
